import { useState, useEffect } from 'react';

interface UpdateNotificationProps {
  onUpdate?: () => void;
}

export default function UpdateNotification({ onUpdate }: UpdateNotificationProps) {
  const [showUpdate, setShowUpdate] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    // Check for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg);
        
        // Check for updates every 30 minutes
        setInterval(() => {
          reg.update();
        }, 30 * 60 * 1000);

        // Listen for updates
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
                setShowUpdate(true);
              }
            });
          }
        });
      });

      // Listen for controller change (new service worker activated)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    }

    // Check version from localStorage
    const currentVersion = '1.5.0';
    const lastSeenVersion = localStorage.getItem('app_version');
    
    if (!lastSeenVersion || lastSeenVersion !== currentVersion) {
      setShowUpdate(true);
      localStorage.setItem('app_version', currentVersion);
    }
  }, []);

  const handleUpdate = () => {
    if (registration && registration.waiting) {
      // Tell the service worker to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // Just reload if no service worker update
      window.location.reload();
    }
    
    if (onUpdate) {
      onUpdate();
    }
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  if (!showUpdate) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md animate-slide-up">
      <div className="bg-gradient-to-br from-cyan-500/10 to-purple-600/10 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-refresh-line text-white text-xl"></i>
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm mb-1">
              {updateAvailable ? 'Update Available!' : 'What\'s New'}
            </h3>
            <p className="text-gray-300 text-xs mb-3">
              {updateAvailable 
                ? 'A new version of PepCoin is ready. Update now for the latest features and improvements.'
                : 'Check out the latest improvements and features we\'ve added to enhance your experience.'}
            </p>
            
            {/* Update Features */}
            <div className="space-y-1.5 mb-3">
              <div className="flex items-center gap-2 text-xs text-gray-300">
                <i className="ri-check-line text-green-400"></i>
                <span>Enhanced security features</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-300">
                <i className="ri-check-line text-green-400"></i>
                <span>Improved performance</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-300">
                <i className="ri-check-line text-green-400"></i>
                <span>Bug fixes and optimizations</span>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleUpdate}
                className="flex-1 px-3 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-xs font-medium hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {updateAvailable ? 'Update Now' : 'Got It'}
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-2 bg-white/10 rounded-lg text-gray-300 text-xs font-medium hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
              >
                Later
              </button>
            </div>
          </div>
          
          {/* Close Button */}
          <button
            onClick={handleDismiss}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
